import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { completeAccountPasswordResetFlow } from 'hooks/public-account-requests';

import LoginLayout from 'containers/login/login-layout';
import Input from 'components/partials/input/input';
import Button from 'components/partials/button/button';
import toast from 'components/partials/toast/toast';
import Heading from 'components/partials/heading/heading';

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain a lowercase letter')
    .matches(/[A-Z]/, 'Password must contain an uppercase letter')
    .matches(/[0-9]/, 'Password must contain a number')
    .matches(/[#?!@$%^&*-]/, 'Password must contain a symbol'),
  // TODO: Wave 8 — Yup's `oneOf` typings reject the legacy `null` sentinel.
  // The runtime semantics rely on it for cross-field comparison; cast
  // preserves behaviour until we revisit the form layer.
  passwordConfirmation: Yup.string()
    .oneOf(
      [Yup.ref('password'), null] as unknown as string[],
      'Must match password'
    )
    .required('Password confirmation is required'),
});

// Inferring from the schema avoids manual-type / schema drift that breaks
// `yupResolver`'s generic constraint.
type ResetPasswordFormValues = Yup.InferType<typeof validationSchema>;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = queryString.parse(location.search);
  // queryString returns `string | string[] | null`; preserve the original
  // single-string semantics that the API contract expects.
  const key = Array.isArray(params.key) ? params.key[0] : params.key;

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      password: '',
      passwordConfirmation: '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues): Promise<void> => {
    try {
      await completeAccountPasswordResetFlow({
        key: key ?? '',
        newPassword: values.password,
      });
      toast.success({
        title: 'Success!',
        message: 'Your password has been changed. Please login to continue.',
      });

      navigate('/login');
    } catch (error) {
      toast.error({
        title: 'Activation error',
        message:
          (error as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ||
          'There was an error when changing your password. Please try again or contact support.',
      });
    }
  };

  return (
    <LoginLayout>
      <Heading
        variant="h2"
        customClass="font-semibold mb-4 block text-center mobile:text-xl tablet:text-3xl"
      >
        Choose a password
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              type="password"
              label="Password"
              placeholder="Enter your password"
              errorsNames={error}
              data-testid="reset-password_password-input"
              labelInTop={true}
              helperText="At least 8 characters, with 1 number and 1 special character"
            />
          )}
        />
        <Controller
          name="passwordConfirmation"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              type="password"
              label="Confirm Password"
              placeholder="Confirm your password"
              errorsNames={error}
              data-testid="reset-password_confirmation-input"
              labelInTop={true}
            />
          )}
        />
        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            variant="brand-filled"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </Button>
        </div>
      </form>
    </LoginLayout>
  );
};

export default ResetPassword;
